import { useEffect, useState, useCallback } from "react";
import { BadgePercent, Search, Plus, Trash2, Package } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminSponsored() {
  const [slots, setSlots] = useState([]);
  const [max, setMax] = useState(6);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [dates, setDates] = useState({ starts_at: "", ends_at: "" });

  const load = useCallback(() => {
    api.get("/marketplace/admin/sponsored").then((r) => { setSlots(r.data.slots || []); setMax(r.data.max || 6); }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const search = () => {
    if (!q.trim()) { setResults([]); return; }
    api.get(`/marketplace/public/products?q=${encodeURIComponent(q)}&page_size=12`).then((r) => setResults(r.data.products || [])).catch(() => setResults([]));
  };
  const add = async (pid) => {
    try { await api.post("/marketplace/admin/sponsored", { product_id: pid, starts_at: dates.starts_at || null, ends_at: dates.ends_at || null }); toast.success("Product sponsored"); setResults([]); setQ(""); setDates({ starts_at: "", ends_at: "" }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (pid) => {
    try { await api.delete(`/marketplace/admin/sponsored/${pid}`); toast.success("Removed from sponsored"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const full = slots.filter((s) => s.active).length >= max;

  return (
    <div data-testid="admin-sponsored">
      <PageHeader title="Sponsored Slots" icon={BadgePercent}
        subtitle="Admin-managed promoted placements. Clearly labelled, capped, and limited to approved sellers with published products." />

      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="sponsored-current">
        <p className="mb-3 text-sm font-semibold text-slate-700">Active slots ({slots.filter((s) => s.active).length}/{max})</p>
        {slots.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="sponsored-empty">No sponsored products yet. Search below to promote a published product.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {slots.map((s) => (
              <div key={s.product_id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-2.5" data-testid={`sponsored-slot-${s.product_id}`}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">{s.image ? <img src={s.image} alt="" className="h-full w-full object-cover" /> : <Package className="h-5 w-5 text-slate-300" />}</span>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-1 text-sm font-medium">{s.product_name}</p>
                  <p className="text-xs text-slate-400">{s.price ? `₹${Math.round(s.price)}` : ""}{s.product_status !== "published" && <span className="ml-1 font-semibold text-rose-500">· {s.product_status} (not shown live)</span>}</p>
                  {(s.starts_at || s.ends_at) && <p className="text-[11px] text-slate-400" data-testid={`sponsored-window-${s.product_id}`}>{s.starts_at ? `From ${s.starts_at}` : "Live now"}{s.ends_at ? ` → ${s.ends_at}` : ""}{s.live === false && <span className="ml-1 font-semibold text-amber-600">· {s.ends_at && new Date().toISOString().slice(0, 10) > s.ends_at ? "expired" : "scheduled"}</span>}</p>}
                </div>
                <button onClick={() => remove(s.product_id)} data-testid={`sponsored-remove-${s.product_id}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="mb-3 text-sm font-semibold text-slate-700">Add a product to a sponsored slot</p>
        {full && <p className="mb-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-700">All {max} slots are in use. Remove one above before adding another.</p>}
        <div className="flex gap-2">
          <div className="relative flex-1"><Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search published products by name / brand" className="pl-9" data-testid="sponsored-search" /></div>
          <button onClick={search} className="rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white" data-testid="sponsored-search-btn">Search</button>
        </div>
        <div className="mt-2 flex flex-wrap items-end gap-3" data-testid="sponsored-dates">
          <label className="text-xs text-slate-500">Start date (optional)<input type="date" value={dates.starts_at} onChange={(e) => setDates({ ...dates, starts_at: e.target.value })} data-testid="sponsored-start" className="mt-0.5 block rounded-lg border border-slate-200 px-3 py-1.5 text-sm" /></label>
          <label className="text-xs text-slate-500">End date — auto-expires (optional)<input type="date" value={dates.ends_at} onChange={(e) => setDates({ ...dates, ends_at: e.target.value })} data-testid="sponsored-end" className="mt-0.5 block rounded-lg border border-slate-200 px-3 py-1.5 text-sm" /></label>
          <span className="text-[11px] text-slate-400">Leave blank to run indefinitely until removed.</span>
        </div>
        {results.length > 0 && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((p) => (
              <div key={p.product_id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-2.5" data-testid={`sponsored-result-${p.product_id}`}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">{(p.image || (p.images || [])[0]) ? <img src={p.image || p.images[0]} alt="" className="h-full w-full object-cover" /> : <Package className="h-5 w-5 text-slate-300" />}</span>
                <div className="min-w-0 flex-1"><p className="line-clamp-1 text-sm font-medium">{p.name}</p><p className="text-xs text-slate-400">₹{Math.round(p.price || 0)}</p></div>
                <button onClick={() => add(p.product_id)} disabled={full} data-testid={`sponsored-add-${p.product_id}`} className="inline-flex items-center gap-1 rounded-full brand-gradient-bg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"><Plus className="h-3.5 w-3.5" />Sponsor</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
